import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useWorkspace } from './WorkspaceContext';
import * as machineService from '../services/machineService';
import type { Machine } from '@jigflow/shared';

interface MachineContextValue {
  machines: Machine[];
  activeMachine: Machine | null;
  loading: boolean;
  error: string | null;
  addMachine(data: Partial<Machine>): Promise<Machine>;
  updateMachine(id: string, data: Partial<Machine>): Promise<void>;
  deleteMachine(id: string): Promise<void>;
  setActiveMachine(id: string): Promise<void>;
  refreshMachines(): Promise<void>;
}

const MachineContext = createContext<MachineContextValue | null>(null);

export function MachineProvider({ children }: { children: React.ReactNode }) {
  const { workspace } = useWorkspace();
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const workspaceId = workspace?.id;

  const fetchMachines = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await machineService.getMachines(workspaceId);
      setMachines(data);
    } catch (err) {
      console.error('[Machine] Failed to fetch:', err);
      setError(err instanceof Error ? err.message : 'Failed to load machines');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchMachines();
  }, [fetchMachines]);

  const activeMachine = machines.find((m) => m.active) || machines[0] || null;

  const addMachine = useCallback(
    async (data: Partial<Machine>) => {
      if (!workspaceId) throw new Error('No workspace');
      const created = await machineService.createMachine(workspaceId, data);
      setMachines((prev) => [...prev, created]);
      return created;
    },
    [workspaceId]
  );

  const updateMachine = useCallback(
    async (id: string, data: Partial<Machine>) => {
      const updated = await machineService.updateMachine(id, data);
      setMachines((prev) => prev.map((m) => (m.id === id ? updated : m)));
    },
    []
  );

  const deleteMachine = useCallback(
    async (id: string) => {
      await machineService.deleteMachine(id);
      setMachines((prev) => prev.filter((m) => m.id !== id));
    },
    []
  );

  const setActiveMachine = useCallback(
    async (id: string) => {
      // Deactivate all, activate selected
      const updates = machines.map((m) =>
        machineService.updateMachine(m.id, { active: m.id === id })
      );
      await Promise.all(updates);
      setMachines((prev) =>
        prev.map((m) => ({ ...m, active: m.id === id }))
      );
    },
    [machines]
  );

  return (
    <MachineContext.Provider
      value={{
        machines,
        activeMachine,
        loading,
        error,
        addMachine,
        updateMachine,
        deleteMachine,
        setActiveMachine,
        refreshMachines: fetchMachines,
      }}
    >
      {children}
    </MachineContext.Provider>
  );
}

export function useMachines() {
  const context = useContext(MachineContext);
  if (!context) {
    throw new Error('useMachines must be used within a MachineProvider');
  }
  return context;
}
